// Results: distance, fuel, efficiency against the stock baseline, credits earned, personal best, events log,
// and the submission to the global board (automatic while Settings → "Submit runs to the global board" is on).
import type { AppContext, ScreenHandle } from './app';
import type { RunResult } from '../types';
import { h, clear, button, cell } from './dom';
import { fmtNm, fmtHours, fmtFuel, fmtIndex, fmtCredits, fmtSeedDate } from './format';
import { craftBuild, fmtEfficiency } from './build';
import { isConfigured } from '../net/supabase';
import { submitRun } from '../net/board';
import { enqueuePending } from '../net/pending';

export function mountResults(root: HTMLElement, ctx: AppContext): ScreenHandle {
  const title = h('h1', { id: 'results-title' }, 'Results');
  const sub = h('p');
  const badges = h('div.actions');
  const stats = h('div.stats');
  const board = h('p.board-status', { role: 'status', 'aria-live': 'polite' });
  const events = h('div.panel');
  const actions = h('div.actions', { style: 'margin-top:1.25rem' });
  root.append(h('div.wrap', {}, h('header.topbar', {}, h('div.title', {}, title, sub), badges), stats, board, events, actions));

  /** The result already handed to the board, so re-rendering the screen never submits twice. */
  let submittedFor: RunResult | null = null;

  /** Paints the board line only while this result is still the one on screen. */
  function setStatus(r: RunResult, text: string): void {
    if (ctx.session.result === r) board.textContent = text;
  }

  function queue(r: RunResult): void {
    ctx.save = enqueuePending(ctx.save, r);
    ctx.persist();
  }

  async function submit(r: RunResult): Promise<void> {
    if (!ctx.save.settings.online) { setStatus(r, 'Global board is off in Settings.'); return; }
    if (!isConfigured) { setStatus(r, 'Board unavailable.'); return; }
    setStatus(r, 'Submitting to the global board…');
    const out = await submitRun(r, ctx.save.settings.nickname, ctx.build);
    if (out.ok) {
      setStatus(r, out.rank === null ? 'Submitted to the global board.' : `Global board: #${out.rank} today on this craft.`);
      void ctx.syncBoard();
      return;
    }
    switch (out.reason) {
      case 'offline': queue(r); setStatus(r, 'Offline — will submit when you are back online.'); break;
      case 'error': queue(r); setStatus(r, 'Board unavailable — will retry later.'); break;
      case 'rejected': setStatus(r, 'Not accepted by the board.'); break;
      case 'disabled': setStatus(r, 'Global board is off in Settings.'); break;
      default: setStatus(r, 'Board unavailable.');
    }
  }

  function render(): void {
    const r = ctx.session.result;
    clear(badges); clear(stats); clear(events); clear(actions);
    if (!r) {
      sub.textContent = '';
      board.hidden = true;
      stats.append(h('p.state', {}, 'No run to show yet. Finish a run and the result appears here.'));
      actions.append(button('Hangar', () => ctx.go('hangar'), { cls: 'btn btn-primary' }));
      return;
    }
    const b = craftBuild(ctx.save, r.craftId);
    const domain = b?.craft.domain ?? 'air';
    const big = b?.big ?? true;
    title.textContent = `Results · ${b?.craft.className ?? r.craftId}`;
    sub.textContent = `${r.endedBy === 'dry-tank' ? 'Dry tank' : 'Ended by you'} · route ${fmtSeedDate(r.seed)} · ${r.modIds.length ? `${r.modIds.length} mod${r.modIds.length > 1 ? 's' : ''}` : 'stock build'}`;
    if (r.isBest) badges.append(h('span.badge.badge-best', {}, 'Personal best'));
    const dist = cell('Distance', fmtNm(r.distanceNm));
    dist.root.classList.add('hero');
    stats.append(
      dist.root,
      cell('Fuel used', fmtFuel(r.fuelUsedKg, domain, big)).root,
      cell('Sim time', fmtHours(r.simHours)).root,
      cell('Efficiency', fmtEfficiency(r.efficiency, domain, big)).root,
      cell('vs stock baseline', `${fmtIndex(r.efficiencyIndex)}`).root,
      cell('Credits earned', fmtCredits(r.credits)).root,
      cell('Balance', fmtCredits(ctx.save.credits)).root,
    );
    board.hidden = false;
    if (submittedFor !== r) {
      submittedFor = r;
      board.textContent = '';
      void submit(r);
    }
    events.append(h('h2', {}, 'Events'));
    if (!r.events.length) events.append(h('p.state', {}, 'No events were logged.'));
    else {
      const ul = h('ul.list.events');
      for (const e of r.events) ul.append(h('li', {}, h('span', {}, fmtHours(e.timeS / 3600)), h('span', {}, fmtNm(e.distanceNm)), h('span', {}, e.text)));
      events.append(ul);
    }
    actions.append(
      button('Run again', () => ctx.go('briefing', { craftId: r.craftId }), { cls: 'btn btn-primary' }),
      button('Global board', () => ctx.go('leaderboard', { craftId: r.craftId }), { cls: 'btn' }),
      button('Hangar', () => ctx.go('hangar'), { cls: 'btn' }),
    );
  }

  return { show: render };
}
